import React, { useEffect, useState } from "react";
import axios from "axios";
import { useApp } from "../context/AppContext";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

const statusColor = { pending: "bg-[var(--accent-2)]", confirmed: "bg-blue-400", ready: "bg-green-400", completed: "bg-[var(--ink)] text-[var(--paper)]", cancelled: "bg-red-400" };

export default function MyOrders() {
  const { API, user, authHeaders } = useApp();
  const [orders, setOrders] = useState([]);
  const [params, setParams] = useSearchParams();
  const [paymentMsg, setPaymentMsg] = useState(null);

  const load = () => {
    if (user) axios.get(`${API}/orders/mine`, { headers: authHeaders() }).then(r=>setOrders(r.data));
  };

  useEffect(() => { load(); }, [user]);

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid || !user) return;
    setPaymentMsg({ kind: "pending", text: "Verifying your payment..." });
    let attempts = 0;
    const poll = async () => {
      if (attempts >= 6) {
        setPaymentMsg({ kind: "error", text: "Payment status check timed out. Contact us if you were charged." });
        return;
      }
      attempts++;
      try {
        const r = await axios.get(`${API}/checkout/status/${sid}`, { headers: authHeaders() });
        if (r.data.payment_status === "paid") {
          setPaymentMsg({ kind: "success", text: `Payment successful! Order ${r.data.order_id} confirmed.` });
          toast.success("Payment received");
          load();
          setParams({}, { replace: true });
          return;
        }
        if (r.data.status === "expired") {
          setPaymentMsg({ kind: "error", text: "Payment session expired." });
          setParams({}, { replace: true });
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        setTimeout(poll, 2000);
      }
    };
    poll();
  }, [params.get("session_id"), user]);

  if (!user) return <div className="p-20 text-center"><p className="font-mono mb-4">Please sign in to view orders.</p><Link to="/login" className="btn-primary">SIGN IN</Link></div>;

  return (
    <div className="max-w-5xl mx-auto px-6 py-10" data-testid="my-orders-page">
      <h1 className="font-display text-5xl mb-8">MY ORDERS</h1>
      {paymentMsg && (
        <div className={`border-2 border-[var(--ink)] p-4 mb-6 font-mono text-sm ${paymentMsg.kind==='success'?'bg-green-200':paymentMsg.kind==='error'?'bg-red-200':'bg-[var(--accent-2)]'}`} data-testid="payment-msg">
          {paymentMsg.text}
        </div>
      )}
      {orders.length === 0 ? <div className="text-center py-20 font-mono" data-testid="no-orders">No orders yet.</div> :
        <div className="space-y-4">
          {orders.map(o => (
            <div key={o.order_id} className="card-industrial p-5" data-testid={`order-${o.order_id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <div>
                  <div className="font-mono text-xs text-[var(--ink-2)]">ORDER #{o.order_id}</div>
                  <div className="font-mono text-xs">{new Date(o.created_at).toLocaleString()}</div>
                </div>
                <div className="flex items-center gap-2">
                  {o.payment_status === "paid" && <span className="px-2 py-1 bg-green-400 border-2 border-[var(--ink)] font-mono text-[10px] font-bold">PAID</span>}
                  <span className={`px-3 py-1 font-mono text-xs uppercase font-bold border-2 border-[var(--ink)] ${statusColor[o.status]||''}`}>{o.status}</span>
                </div>
              </div>
              <div className="space-y-1 mb-3">
                {o.items.map((i,idx) => <div key={`${i.product_id}-${idx}`} className="font-mono text-sm flex justify-between"><span>{i.name} ×{i.qty}</span><span>${(i.price*i.qty).toFixed(2)}</span></div>)}
              </div>
              <div className="flex justify-between font-display text-xl border-t-2 border-[var(--ink)] pt-2"><span>TOTAL · {o.payment_method.replace('_',' ')}</span><span>${o.total.toFixed(2)}</span></div>
            </div>
          ))}
        </div>
      }
    </div>
  );
}
